import { useState, useEffect, useCallback } from 'react';
import { updateService, AppVersions } from '../services'; // Import AppVersions
import { useNotifications } from '../contexts/NotificationContext';

/**
 * Hook to check for application updates and show a notification when an update is available
 * @param checkIntervalMs How often to check for updates (in milliseconds)
 * @returns Object with update status and functions to check for and apply updates
 */
export const useAppUpdate = (checkIntervalMs = 60000) => {
  const [updateAvailable, setUpdateAvailable] = useState<boolean>(false);
  const [newVersions, setNewVersions] = useState<AppVersions | null>(null); // Changed state name and type
  const [notifiedVersions, setNotifiedVersions] = useState<AppVersions | null>(null);
  const { showNotification } = useNotifications();

  // Function to check for updates manually
  const checkForUpdates = useCallback(async () => {
    // updateService.checkForUpdates() will internally call the onUpdate callback
    // if an update is found and conditions are met (e.g., not DEV mode without simulation).
    // The onUpdate callback (defined in useEffect below) is responsible for setting
    // updateAvailable and newVersions states.
    const hasUpdate = await updateService.checkForUpdates();
    // The return value indicates if an update was found and (if in DEV) simulated.
    return hasUpdate;
  }, []);

  // Function to apply the update (reload the page)
  const applyUpdate = useCallback(() => {
    if (newVersions) {
      updateService.applyUpdate(newVersions); // Pass AppVersions object
    } else {
      // Fallback: should not happen if UI only shows 'Reload' when newVersions is set.
      console.warn('Attempted to apply update without new versions. Reloading page.');
      // Consider if a generic reload is the best fallback or if an error should be shown.
      // For now, keeping it simple with a reload.
      window.location.reload();
    }
  }, [newVersions]); // Depends on newVersions from state

  // Function to simulate a new build (for testing in development)
  const simulateNewBuild = useCallback(() => {
    if (import.meta.env.DEV) {
      updateService.simulateNewBuild();
    }
  }, []);

  // Show a notification when an update is available
  useEffect(() => {
    if (updateAvailable && newVersions) {
      if (
        !notifiedVersions ||
        notifiedVersions.dataStateVersion !== newVersions.dataStateVersion ||
        notifiedVersions.filesVersion !== newVersions.filesVersion
      ) {
        showNotification(
          'info',
          'A new version of the application is available.',
          -1, // Use -1 to indicate that the notification should never auto-dismiss
          [
            {
              label: 'Reload Now',
              onClick: applyUpdate,
              primary: true,
            },
          ]
        );
        setNotifiedVersions(newVersions);
      }
    }
  }, [updateAvailable, newVersions, showNotification, applyUpdate, notifiedVersions]); // Depend on newVersions and notifiedVersions

  // Set up the update service
  useEffect(() => {
    // Register the callback for when an update is available
    updateService.onUpdate((versions) => { // Callback receives AppVersions
      setUpdateAvailable(true);
      setNewVersions(versions); // Set AppVersions object
    });

    // Start checking for updates periodically
    updateService.startPeriodicChecks(checkIntervalMs);

    // Check for updates immediately
    checkForUpdates();

    // Clean up when the component unmounts
    return () => {
      updateService.stopPeriodicChecks();
    };
  }, [checkIntervalMs, checkForUpdates]);

  return {
    updateAvailable,
    newVersions, // Return newVersions
    checkForUpdates,
    applyUpdate,
    simulateNewBuild,
  };
};
